import { useEffect, useRef, useState } from 'react';
import type { EntradaDeAuditoria } from '../../api/ops/backoffice';
import { ErrorApi } from '../../api/problema';
import { useAvisoBreve } from '../../componentes/avisoBreve';
import { Boton } from '../../componentes/Boton';
import { EstadoCargando, EstadoError } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { faltanDosPasos } from '../../logica/acceso';
import { estadoDeError } from '../../logica/estados';
import { usePaginacionCursor } from '../../logica/paginacion';
import { SinPermiso } from '../SinPermiso';
import type { PropsSeccion } from '../tipos';
import { siglas, textoDeError } from '../negocios/logica';
import { PESTANAS, acciones, accionesDelNegocio, fraseDe, lineaHora, porDias, sinResultados, textoBusqueda, textoCadena, type ResultadoCadena } from './logica';
import './auditoria.css';

/**
 * **Auditoría de plataforma** (1.21, `listPlatformAudit`, G183): lo que hace el equipo de Reservaria, por días, con las
 * pestañas Todo · Accesos · Negocios · Precios · Partners · YCloud, la lupa (persona, negocio o motivo; a la API desde 2
 * letras) y «Anterior / Siguiente» (G121). No se edita ni se borra. El Soporte solo ve la del negocio en el que tiene un
 * acceso abierto (lo filtra la API).
 *
 * Cada entrada trae el nombre del negocio (`business_name`, G1140) y el rol de quien actúa («· Soporte» o «· Superadmin»,
 * `actor.platform_role`, G1141). Con la auditoría filtrada por un negocio (`?negocio=`: al tocar su nombre en una entrada
 * o desde «Ver su auditoría» en la ficha del negocio, G1173), «Comprobar la cadena»
 * (`verifyAuditChain`, G1144) dice si está intacta o desde qué entrada está rota.
 */
interface Pagina {
    data: EntradaDeAuditoria[];
    next_cursor: string | null;
    /** Con `business_id`, cuántas acciones hay de ese negocio (G1290); sin él, nulo. */
    total_count?: number | null;
}

export function Auditoria(p: PropsSeccion) {
    const { ops, superadmin, movil, busqueda, cambiarConsulta, onSesionCaducada, onFaltanDosPasos } = p;
    const negocio = busqueda.get('negocio');
    const [pestana, setPestana] = useState(0);
    const [texto, setTexto] = useState('');
    const [buscar, setBuscar] = useState<string | undefined>(undefined);
    const [lupa, setLupa] = useState(false);
    const [vuelta, setVuelta] = useState(0);
    const pag = usePaginacionCursor();
    const { reiniciar } = pag;
    const [estado, setEstado] = useState<{ clave: string; pagina: Pagina | null; error: unknown }>({ clave: '', pagina: null, error: null });
    const categoria = PESTANAS[pestana]?.clave ?? null;

    useEffect(() => {
        const reloj = setTimeout(() => {
            setBuscar(textoBusqueda(texto));
        }, 300);
        return () => {
            clearTimeout(reloj);
        };
    }, [texto]);
    const primera = useRef(true);
    useEffect(() => {
        if (primera.current) {
            primera.current = false;
            return;
        }
        reiniciar();
    }, [categoria, buscar, negocio, reiniciar]);

    const clave = JSON.stringify([categoria, buscar, negocio, pag.cursor, vuelta]);
    useEffect(() => {
        let vivo = true;
        ops.listPlatformAudit({ business_id: negocio ?? undefined, category: categoria ?? undefined, q: buscar, cursor: pag.cursor, limit: 25 })
            .then((pagina) => {
                if (vivo) {
                    setEstado({ clave, pagina, error: null });
                }
            })
            .catch((error: unknown) => {
                if (!vivo) {
                    return;
                }
                if (error instanceof ErrorApi && error.estado === 401) {
                    onSesionCaducada();
                    return;
                }
                if (faltanDosPasos(error)) {
                    onFaltanDosPasos();
                    return;
                }
                setEstado({ clave, pagina: null, error });
            });
        return () => {
            vivo = false;
        };
    }, [ops, categoria, buscar, negocio, pag.cursor, vuelta, clave, onSesionCaducada, onFaltanDosPasos]);

    const lista = estado.clave === clave ? estado.pagina : null;
    const error = estado.clave === clave ? estado.error : null;
    const ahora = new Date();
    const nombreNegocio = negocio === null ? null : (lista?.data.find((e) => e.business_id === negocio && (e.business_name ?? '') !== '')?.business_name ?? null);
    const filtrar = (id: string | null) => {
        cambiarConsulta({ negocio: id });
    };

    if (error !== null && estadoDeError(error).tipo === 'sin-permiso') {
        return <SinPermiso seccion="Auditoría" superadmin={superadmin} movil={movil} onIr={null} />;
    }
    return (
        <>
            <div className="rv-bo-herramientas">
                <div className="rv-bo-herramientas__filtros">
                    <Filtros items={PESTANAS.map((x) => ({ etiqueta: x.etiqueta }))} valor={pestana} etiqueta="Qué auditoría ver" onCambio={setPestana} />
                </div>
                <div className="rv-bo-buscar">
                    {lupa || texto !== '' ? (
                        <label className="rv-bo-buscar__caja">
                            <Icono nombre="search" />
                            <input
                                type="search"
                                aria-label="Buscar persona, negocio o motivo"
                                placeholder="Buscar persona, negocio o motivo"
                                name="buscar-auditoria"
                                value={texto}
                                autoComplete="off"
                                // Se abre al tocar la lupa: el foco va al campo.
                                autoFocus={lupa}
                                onChange={(e) => {
                                    setTexto(e.target.value);
                                }}
                            />
                            <button
                                type="button"
                                className="rv-bo-buscar__quitar"
                                aria-label="Cerrar la búsqueda"
                                onClick={() => {
                                    setTexto('');
                                    setLupa(false);
                                }}
                            >
                                <Icono nombre="x" />
                            </button>
                        </label>
                    ) : (
                        <button
                            type="button"
                            className="rv-bi rv-bo-lupa"
                            aria-label="Buscar en la auditoría"
                            onClick={() => {
                                setLupa(true);
                            }}
                        >
                            <Icono nombre="search" />
                        </button>
                    )}
                </div>
            </div>
            {negocio !== null && <FiltroNegocio key={negocio} {...p} negocio={negocio} nombre={nombreNegocio} total={lista?.total_count ?? null} onQuitar={() => { filtrar(null); }} />}
            {error !== null ? (
                <EstadoError seccion="Auditoría" codigoSoporte={estadoDeError(error).codigoSoporte} movil={movil} onReintentar={() => {
                    setVuelta((v) => v + 1);
                }} />
            ) : lista === null ? (
                <EstadoCargando seccion="Auditoría" />
            ) : (
                <>
                    {lista.data.length === 0 && buscar !== undefined && <div className="rv-bo-auditoria__nada">{sinResultados(buscar)}</div>}
                    <div className="rv-bo-auditoria">
                        {porDias(lista.data, ahora).map((dia) => (
                            <section key={dia.etiqueta} className="rv-bo-auditoria__dia" aria-label={dia.etiqueta}>
                                <div className="rv-bo-auditoria__cab">
                                    <strong>{dia.etiqueta}</strong>
                                    <span>{acciones(dia.entradas.length)}</span>
                                </div>
                                <div className="rv-bo-auditoria__tarjeta">
                                    {dia.entradas.map((e, j) => {
                                        const f = fraseDe(e, e.business_name ?? undefined);
                                        const id = e.business_id ?? null;
                                        return (
                                            <div key={e.id} className="rv-bo-auditoria__fila" style={{ animationDelay: `${String(j * 40)}ms` }}>
                                                <span className={`rv-bo-auditoria__baldosa${f.roja ? ' rv-bo-auditoria__baldosa--roja' : ''}`}>
                                                    <Icono nombre={f.icono} />
                                                </span>
                                                <span className="rv-bo-auditoria__textos">
                                                    <span className="rv-bo-auditoria__frase">
                                                        <strong>{f.actor}</strong> {f.verbo}{' '}
                                                        {/* C.75 (G1173): tocar el negocio (subrayado) filtra la auditoría por él. */}
                                                        {f.objeto !== '' && id !== null && negocio === null && (e.business_name ?? '') !== '' ? (
                                                            <button
                                                                type="button"
                                                                className="rv-bo-auditoria__negocio"
                                                                aria-label={`Ver solo la auditoría de ${f.objeto}`}
                                                                onClick={() => {
                                                                    filtrar(id);
                                                                }}
                                                            >
                                                                {f.objeto}
                                                            </button>
                                                        ) : (
                                                            f.objeto !== '' && <strong>{f.objeto}</strong>
                                                        )}
                                                    </span>
                                                    {f.motivo !== '' && <span className="rv-bo-auditoria__motivo">{f.motivo}</span>}
                                                    <span className="rv-bo-auditoria__hora">{lineaHora(e)}</span>
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </section>
                        ))}
                        <Paginacion
                            hayAnterior={pag.hayAnterior}
                            haySiguiente={typeof lista.next_cursor === 'string' && lista.next_cursor !== ''}
                            movil={movil}
                            onAnterior={pag.anterior}
                            onSiguiente={() => {
                                pag.siguiente(lista.next_cursor);
                            }}
                        />
                    </div>
                </>
            )}
        </>
    );
}

/**
 * La auditoría filtrada por un negocio, con «Comprobar la cadena» (`verifyAuditChain`, G1144) y su resultado: intacta,
 * con las entradas comprobadas, o rota desde la entrada N.
 */
// C.65 y C.75 (G1144, G1173): franja con las siglas y el negocio, «Comprobar la cadena» y «Quitar el filtro».
function FiltroNegocio({ ops, movil, negocio, nombre, total, onQuitar }: PropsSeccion & { negocio: string; nombre: string | null; total: number | null; onQuitar: () => void }) {
    const avisar = useAvisoBreve();
    const [ocupado, setOcupado] = useState(false);
    const [resultado, setResultado] = useState<ResultadoCadena | null>(null);
    const comprobar = () => {
        if (ocupado) {
            return;
        }
        setOcupado(true);
        setResultado(null);
        ops.verifyAuditChain(negocio)
            .then((r) => {
                setResultado(r);
            })
            .catch((e: unknown) => {
                avisar({ tipo: 'error', texto: textoDeError(e, 'No se ha podido comprobar la cadena. Prueba de nuevo.') });
            })
            .finally(() => {
                setOcupado(false);
            });
    };
    return (
        <div className="rv-bo-auditoria__filtro" role="region" aria-label="Auditoría de un negocio">
            <div className="rv-bo-auditoria__filtro-fila">
                <span className="rv-bo-auditoria__filtro-nombre">
                    {nombre !== null && <span className="rv-bo-auditoria__filtro-siglas" aria-hidden="true">{siglas(nombre)}</span>}
                    <span className="rv-bo-auditoria__filtro-texto">
                        <strong>{`Solo ${nombre ?? 'este negocio'}`}</strong>
                        {accionesDelNegocio(total) !== null && <small>{accionesDelNegocio(total)}</small>}
                    </span>
                </span>
                <span className="rv-bo-auditoria__filtro-botones">
                    <Boton tipo="secundario" icono="shield-check" movil={movil} ocupado={ocupado} ocupadoTexto="Comprobando…" onClick={comprobar}>
                        Comprobar la cadena
                    </Boton>
                    <Boton tipo="fantasma" icono="x" movil={movil} onClick={onQuitar}>
                        Quitar el filtro
                    </Boton>
                </span>
            </div>
            {resultado !== null && (
                <div role="status" className={`rv-bo-auditoria__cadena${resultado.intact ? '' : ' rv-bo-auditoria__cadena--rota'}`}>
                    <Icono nombre={resultado.intact ? 'shield-check' : 'shield-alert'} />
                    <span>
                        <strong>{textoCadena(resultado).titulo}</strong>
                        <span>{textoCadena(resultado).texto}</span>
                    </span>
                </div>
            )}
        </div>
    );
}
